import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Loader from './components/Loader';
import { SkeletonPage } from './components/SkeletonLoader';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import { ToastContainer } from './components/Toast';
import JourneyLayout from './components/journey/JourneyLayout';
import { ToastProvider, useToasts } from './hooks/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext'; 
import { ScannerProvider } from './contexts/ScannerContext';
import useUserOnboardingStatus from './hooks/useUserOnboardingStatus';
import SyncStatusBanner from './components/SyncStatusBanner';
import OnlineOnly from './components/OnlineOnly';
import BottomNavBar from './components/BottomNavBar';
import Footer from './components/Footer';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import { AudioProvider } from './contexts/AudioContext';
import GlobalMiniPlayer from './components/GlobalMiniPlayer';
import ChatBot from './components/ChatBot';
import ScrollToTop from './components/ScrollToTop';
import { SocketProvider } from './contexts/SocketContext';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// Library CSS (Global ordering for build optimization)
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';

import './styles/Modal.css';
import './styles/GlobalMiniPlayer.css';
import { requestForToken, onMessageListener } from './notifications';

// Lazy Loaded Pages
const Register = lazy(() => import('./pages/Register'));
const Login = lazy(() => import('./pages/Login'));
const CompleteProfile = lazy(() => import('./pages/CompleteProfile'));
const Profile = lazy(() => import('./pages/Profile'));
const FieldListPage = lazy(() => import('./pages/FieldListPage'));
const AddFieldPage = lazy(() => import('./pages/AddFieldPage'));
const FieldDetailPage = lazy(() => import('./pages/FieldDetailPage'));
const FieldTimelinePage = lazy(() => import('./pages/FieldTimelinePage'));
const AddCropPage = lazy(() => import('./pages/AddCropPage'));
const UserListPage = lazy(() => import('./pages/UserListPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminFieldListPage = lazy(() => import('./pages/AdminFieldListPage'));
const UploadSoilReportPage = lazy(() => import('./pages/UploadSoilReportPage'));
const CropMasterPage = lazy(() => import('./pages/CropMasterPage'));
const GrowthStageMasterPage = lazy(() => import('./pages/GrowthStageMasterPage'));
const FertilizerMasterPage = lazy(() => import('./pages/FertilizerMasterPage'));
const PesticideMasterPage = lazy(() => import('./pages/PesticideMasterPage'));
const InsecticideMasterPage = lazy(() => import('./pages/InsecticideMasterPage'));
const StandardDosagePage = lazy(() => import('./pages/StandardDosagePage'));
const AdminBulkUploadPage = lazy(() => import('./pages/AdminBulkUploadPage'));
const AdminAgronomicMasterPage = lazy(() => import('./pages/AdminAgronomicMasterPage'));
const RecommendationPreviewPage = lazy(() => import('./pages/RecommendationPreviewPage'));
const CropImageGalleryPage = lazy(() => import('./pages/CropImageGalleryPage'));
const AdminRecommendationListPage = lazy(() => import('./pages/AdminRecommendationListPage'));
const AdminRecommendationDetailPage = lazy(() => import('./pages/AdminRecommendationDetailPage'));
const WelcomePage = lazy(() => import('./pages/WelcomePage'));
const WeatherDashboard = lazy(() => import('./pages/WeatherDashboard'));
const MyRecommendationsPage = lazy(() => import('./pages/MyRecommendationsPage'));
const NotificationListPage = lazy(() => import('./pages/NotificationListPage'));
const AdminFieldMonitorPage = lazy(() => import('./pages/AdminFieldMonitorPage'));
const AdminPasswordResetRequests = lazy(() => import('./pages/AdminPasswordResetRequests'));
const AdminNotificationPage = lazy(() => import('./pages/AdminNotificationPage'));
const AdminNotificationHistoryPage = lazy(() => import('./pages/AdminNotificationHistoryPage'));
const AdminImageReviewPage = lazy(() => import('./pages/AdminImageReviewPage'));
const SocialFeedPage = lazy(() => import('./pages/SocialFeedPage'));
const PublicPostPage = lazy(() => import('./pages/PublicPostPage'));
const FertilizerDetailsPage = lazy(() => import('./pages/FertilizerDetailsPage'));
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const AdminBlogListPage = lazy(() => import('./pages/AdminBlogListPage'));
const AdminBlogEditPage = lazy(() => import('./pages/AdminBlogEditPage'));

// Legal Pages
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const TermsConditions = lazy(() => import('./pages/legal/TermsConditions'));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer'));
const CookiePolicy = lazy(() => import('./pages/legal/CookiePolicy'));
const ContactGrievance = lazy(() => import('./pages/legal/ContactGrievance'));

const RoleBasedHome = ({ userInfo }) => {
  const { hasFields, loading } = useUserOnboardingStatus();

  if (!userInfo) {
    return <Navigate to="/login" replace />;
  }
  if (userInfo.role === 'ADMIN') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (loading) {
    return <Loader />;
  }

  if (hasFields) {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/journey/welcome" replace />;
  }
};

const AuthChecker = () => {
  const userInfo = JSON.parse(localStorage.getItem('userInfo'));
  const location = useLocation();

  if (userInfo && userInfo.mustChangePassword && location.pathname !== '/profile') {
    return <Navigate to='/profile' replace />;
  }

  return userInfo ? <Outlet /> : <Navigate to='/login' replace />;
};



const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 1000 * 30, // 30 seconds
            cacheTime: 1000 * 60 * 60 * 24, // 24 hours
            retry: 2,
            refetchOnWindowFocus: false,
        },
    },
});

const persister = createSyncStoragePersister({
    storage: window.localStorage,
});

function App() {
  const [userInfo, setUserInfo] = useState(
    JSON.parse(localStorage.getItem('userInfo'))
  );

  useEffect(() => {
    const handleAuthChange = () => {
      setUserInfo(JSON.parse(localStorage.getItem('userInfo')));
    };

    window.addEventListener('authchange', handleAuthChange);
    return () => window.removeEventListener('authchange', handleAuthChange);
  }, []);

  return (
    <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister }}
    >
        <ThemeProvider>
            <SocketProvider>
                <ToastProvider>
                <AudioProvider>
                    <Router>
                    <ScrollToTop />
                    <ScannerProvider>
                        <div className="flex flex-col min-h-screen">
                        <Header />
                        <SyncStatusBanner />
                        <main className="flex-grow bg-beige-50 dark:bg-sand-950 pb-20 md:pb-0">
                            <Suspense fallback={<SkeletonPage />}>
                            <Routes>
                            <Route path='/login' element={<Login />} />
                            <Route path='/register' element={<Register />} />
                            <Route path='/complete-profile' element={<CompleteProfile />} />
                            <Route path='/post/:id' element={<OnlineOnly featureName="Public Post"><PublicPostPage /></OnlineOnly>} />
                            <Route path='/feed' element={<OnlineOnly featureName="Community Feed"><SocialFeedPage /></OnlineOnly>} />
                            <Route path='/fertilizer/:slug' element={<FertilizerDetailsPage />} />
                            <Route path='/blogs' element={<BlogListPage />} />
                            <Route path='/blog/:slug' element={<BlogDetailPage />} />
                            <Route path='/about' element={<AboutUsPage />} />

                            {/* Legal Routes */}
                            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                            <Route path='/terms' element={<TermsConditions />} />
                            <Route path='/disclaimer' element={<Disclaimer />} />
                            <Route path='/cookie-policy' element={<CookiePolicy />} />
                            <Route path='/contact' element={<ContactGrievance />} />

                            <Route path='/' element={<RoleBasedHome userInfo={userInfo} />} />

                            <Route path='' element={<AuthChecker />}>
                                <Route path='/profile' element={<Profile />} />
                                <Route path='/fields/:id' element={<FieldDetailPage />} />
                                <Route path='/fields/:id/timeline' element={<FieldTimelinePage />} />
                                <Route path='/crops/:cropId/images' element={<CropImageGalleryPage />} />

                                <Route path='journey' element={<JourneyLayout />}>
                                <Route path='welcome' element={<WelcomePage />} />
                                <Route path='field-setup' element={<AddFieldPage />} />
                                <Route path='soil-understanding' element={<UploadSoilReportPage />} />
                                <Route path='crop-health' element={<AddCropPage />} />
                                <Route path='recommendation-preview' element={<RecommendationPreviewPage />} />
                                </Route>
                            </Route>

                            <Route path='' element={<PrivateRoute />}>
                                <Route path='/dashboard' element={<WeatherDashboard />} />
                                <Route path='/fields' element={<FieldListPage />} />
                                <Route path='/recommendations' element={<MyRecommendationsPage />} />
                                <Route path='/notifications' element={<NotificationListPage />} />
                            </Route>

                            <Route path='' element={<AdminRoute />}>
                                <Route path='/admin/dashboard' element={<OnlineOnly featureName="Admin Dashboard"><AdminDashboard /></OnlineOnly>} />
                                <Route path='/admin/farmers' element={<OnlineOnly featureName="Farmer Management"><UserListPage /></OnlineOnly>} />
                                <Route path='/admin/farmers/:userId/fields' element={<OnlineOnly featureName="Farmer Fields"><AdminFieldListPage /></OnlineOnly>} />
                                <Route path='/admin/farmers/:userId/fields/:id' element={<OnlineOnly featureName="Field Details"><FieldDetailPage /></OnlineOnly>} />
                                <Route path='/admin/master/crops' element={<OnlineOnly featureName="Crop Master"><CropMasterPage /></OnlineOnly>} />
                                <Route path='/admin/master/growth-stages' element={<OnlineOnly featureName="Growth Stages"><GrowthStageMasterPage /></OnlineOnly>} />
                                <Route path='/admin/master/fertilizers' element={<OnlineOnly featureName="Fertilizer Master"><FertilizerMasterPage /></OnlineOnly>} />
                                <Route path='/admin/master/pesticides' element={<OnlineOnly featureName="Pesticide Master"><PesticideMasterPage /></OnlineOnly>} />
                                <Route path='/admin/master/insecticides' element={<OnlineOnly featureName="Insecticide Master"><InsecticideMasterPage /></OnlineOnly>} />
                                <Route path='/admin/master/standard-dosages' element={<OnlineOnly featureName="Standard Dosages"><StandardDosagePage /></OnlineOnly>} />
                                <Route path='/admin/master/bulk-upload' element={<OnlineOnly featureName="Bulk Upload"><AdminBulkUploadPage /></OnlineOnly>} />
                                <Route path='/admin/agronomic-master' element={<OnlineOnly featureName="Agronomic Master DB"><AdminAgronomicMasterPage /></OnlineOnly>} />
                                <Route path='/admin/recommendations' element={<OnlineOnly featureName="Recommendations"><AdminRecommendationListPage /></OnlineOnly>} />
                                <Route path='/admin/recommendations/:recommendationId' element={<OnlineOnly featureName="Recommendation Details"><AdminRecommendationDetailPage /></OnlineOnly>} />
                                <Route path='/admin/monitoring' element={<OnlineOnly featureName="Field Monitoring"><AdminFieldMonitorPage /></OnlineOnly>} />
                                <Route path='/admin/requests' element={<OnlineOnly featureName="Password Resets"><AdminPasswordResetRequests /></OnlineOnly>} />
                                <Route path='/admin/notifications' element={<OnlineOnly featureName="Push Notifications"><AdminNotificationPage /></OnlineOnly>} />
                                <Route path='/admin/notifications/history' element={<OnlineOnly featureName="Notification History"><AdminNotificationHistoryPage /></OnlineOnly>} />
                                <Route path='/admin/blogs' element={<OnlineOnly featureName="Manage Blogs"><AdminBlogListPage /></OnlineOnly>} />
                                <Route path='/admin/blogs/create' element={<OnlineOnly featureName="Create Blog"><AdminBlogEditPage /></OnlineOnly>} />
                                <Route path='/admin/blogs/:id/edit' element={<OnlineOnly featureName="Edit Blog"><AdminBlogEditPage /></OnlineOnly>} />
                                <Route path='/admin/image-review' element={<OnlineOnly featureName="Image AI Review"><AdminImageReviewPage /></OnlineOnly>} />
                            </Route>
                            </Routes>
                            </Suspense>
                            {userInfo && userInfo.role === 'FARMER' && (
                            <>
                                <BottomNavBar />
                                <ChatBot />
                                <PWAInstallPrompt />
                            </>
                            )}
                            <GlobalMiniPlayer />
                        </main>
                        <Footer />
                        <AppToastContainer />
                        </div>
                    </ScannerProvider>
                    </Router>
                </AudioProvider>
                </ToastProvider>
            </SocketProvider>
        </ThemeProvider>
    </PersistQueryClientProvider>
  );
}

const AppToastContainer = () => {
  const { toasts, removeToast, addToast } = useToasts();

  useEffect(() => {
    // Use sessionStorage so this runs at most once per browser session,
    // not on every React re-mount or page refresh within the same tab.
    if (!sessionStorage.getItem('notif_init')) {
      requestForToken();
      sessionStorage.setItem('notif_init', '1');
    }

    // Listen for foreground messages (Continuous subscription)
    const unsubscribe = onMessageListener((payload) => {
      // Extract data safely
      const title = payload.notification?.title || payload.data?.title || "Naturextrakte OBFS";
      const body = payload.notification?.body || payload.data?.body || "New notification received";
      const url = payload.data?.url || (payload.fcmOptions?.link);
      
      // Trigger the toast
      addToast(title, body, 'info', url);
    });

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [addToast]);

  return <ToastContainer toasts={toasts} onDismiss={removeToast} />;
}

export default App;
